import { RenderProgress } from "@/components/mist/RenderProgress";
import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Film, Video, Image as ImageIcon, Library, Sparkles, Loader2, X, Check,
  Crop, Stamp, Palette, Smile, Wand2, ArrowLeft,
} from "lucide-react";
import { mistApi, pipelinesApi } from "@/api/mist";
import { ResultCard, AssetPicker } from "@/components/mist/shared";
import { useStudioParam } from "@/hooks/useStudioUrl";
import VaultPickButton from "@/components/mist/VaultPickButton";

/* ===================== FACEBOOK-STYLE OVERLAY COMPOSER ===================== */
function UploadBlock({ testid, label, hint, accept, track, slot, onSet, onClear }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const r = await mistApi.ingest(f, track);
      onSet({ filename: r.filename, previewUrl: URL.createObjectURL(f) });
      toast.success(`${label} loaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div>
      <input ref={ref} type="file" accept={accept} hidden onChange={pick} data-testid={`${testid}-input`} />
      {slot?.filename ? (
        <div className="relative border-2 border-black bg-charcoal p-1 shadow-[3px_3px_0px_#000]" data-testid={`${testid}-preview`}>
          {track === "video"
            ? <video src={slot.previewUrl} muted loop autoPlay playsInline className="w-full h-36 object-cover" />
            : <img src={slot.previewUrl} alt={label} className="w-full h-36 object-contain bg-black" />}
          <button onClick={onClear} data-testid={`${testid}-clear`} className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center border-2 border-black bg-[#141414] shadow-[2px_2px_0px_#000]"><X size={14} /></button>
          <span className="absolute bottom-1.5 left-1.5 font-mono text-[8px] font-black uppercase bg-gold text-black px-2 py-0.5 border border-black truncate max-w-[80%]">{slot.filename}</span>
        </div>
      ) : (
        <div className="space-y-1.5">
          <button onClick={() => ref.current?.click()} disabled={busy} data-testid={testid}
            className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-black bg-[#141414] h-28 shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
            {busy ? <Loader2 size={24} className="animate-spin text-black" /> : (track === "video" ? <Video size={24} className="text-black" /> : <ImageIcon size={24} className="text-black" />)}
            <span className="font-mono text-[10px] font-black uppercase tracking-widest text-black/70 px-3 text-center">{hint}</span>
          </button>
          <VaultPickButton onFile={(f) => pick({ target: { files: [f], value: "" } })} bucket={track === "video" ? "videos" : "images"}
            testid={`${testid}-vault`} disabled={busy} label="From Vault" />
        </div>
      )}
    </div>
  );
}

function FacebookComposer() {
  const [bg, setBg] = useState(null);          // {filename, previewUrl}
  const [overlay, setOverlay] = useState(null);
  const [libOpen, setLibOpen] = useState(false);
  const [bgSel, setBgSel] = useState([]);
  const [ovSel, setOvSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const applyLibrary = () => {
    if (bgSel[0]) setBg({ filename: bgSel[0], previewUrl: null });
    if (ovSel[0]) setOverlay({ filename: ovSel[0], previewUrl: null });
    setLibOpen(false);
    toast.success("Pulled from Mist Library");
  };

  const merge = async () => {
    if (!bg?.filename) { toast.error("Choose a Facebook background reel"); return; }
    if (!overlay?.filename) { toast.error("Choose an animated overlay image"); return; }
    setBusy(true); setResult(null);
    try {
      const { job_id } = await pipelinesApi.stitch("facebook_overlay", bg.filename, overlay.filename);
      const j = await pipelinesApi.poll(job_id);
      if (j.status === "completed" && j.asset_id) {
        setResult({ asset: { id: j.asset_id, track: "video" }, engine: j.engine });
        if (j.engine === "fb_sim") toast("🧪 Facebook Reel Mode Simulation Active", { icon: "🎬" });
        else toast.success("Facebook reel merged");
      } else { toast.error(j.error || "Merge failed"); }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Merge failed"); }
    finally { setBusy(false); }
  };

  if (libOpen) {
    return (
      <div className="h-full w-full flex flex-col" data-testid="fb-library-panel">
        <div className="px-4 pt-3 pb-1">
          <button onClick={() => setLibOpen(false)} data-testid="fb-library-back" className="flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-white/60">
            <ArrowLeft size={13} /> Composer
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 space-y-4">
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-white/60">Background reel (VideoVault)</p>
          <AssetPicker track="video" selected={bgSel} onChange={setBgSel} max={1} testid="fb-lib-video" />
          <p className="font-mono text-[9px] font-black uppercase tracking-widest text-white/60">Animated overlay (ImageVault)</p>
          <AssetPicker track="image" selected={ovSel} onChange={setOvSel} max={1} testid="fb-lib-image" />
        </div>
        <div className="shrink-0 border-t-4 border-[#2a2a2a] bg-[#141414] px-4 py-3">
          <button onClick={applyLibrary} data-testid="fb-library-apply" className="w-full bg-gold text-black font-black font-mono text-xs uppercase tracking-widest py-3 border-2 border-black rounded-full shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none">
            Use Selected Assets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden" data-testid="fb-composer">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 pt-2 space-y-4">
        <p className="font-mono text-[10px] text-white/60 leading-relaxed border-2 border-dashed border-[#2a2a2a]/30 rounded-xl p-2.5">
          Add a moving overlay onto a 30s background reel — Facebook-style. Pick a base reel + overlay, then combine.
        </p>

        <div>
          <span className="block mb-1.5 font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60">Choose Facebook background reel</span>
          <UploadBlock testid="fb-bg" label="Background reel" hint="🎥 Upload 30s background reel" accept="video/*" track="video" slot={bg} onSet={setBg} onClear={() => setBg(null)} />
        </div>

        <div>
          <span className="block mb-1.5 font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60">Choose animated overlay image</span>
          <UploadBlock testid="fb-overlay" label="Animated overlay" hint="🖼️ Upload GIF / sticker / portrait" accept="image/*,image/gif" track="image" slot={overlay} onSet={setOverlay} onClear={() => setOverlay(null)} />
        </div>

        <button onClick={() => setLibOpen(true)} data-testid="fb-pull-library"
          className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#9EFFB8] py-3 font-mono text-[10px] font-black uppercase tracking-widest text-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <Library size={15} /> Pull from Mist Library
        </button>

        <ResultCard result={result} />
      </div>
      <div className="shrink-0 border-t-4 border-[#2a2a2a] bg-[#141414] px-4 py-3">
        <button onClick={merge} disabled={busy || !bg?.filename || !overlay?.filename} data-testid="fb-merge-btn"
          className="w-full flex items-center justify-center gap-2 bg-gold text-black font-black font-mono text-xs uppercase tracking-widest py-3.5 border-2 border-black rounded-full shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Film size={16} />} {busy ? "Merging…" : "⚡ Merge Facebook Reel"}
        </button>
        {busy && (
          <div className="mt-3 flex justify-center"><RenderProgress label="Merging your reel" testid="fb-progress" /></div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ EFFECTS GRID ----------------------------- */
const EFFECTS = [
  { k: "resizer", label: "Resizer", sub: "Reframe & scale", Icon: Crop, color: "#A4DCFA" },
  { k: "watermark", label: "Watermark", sub: "Brand stamp", Icon: Stamp, color: "#FFC9A3" },
  { k: "hue", label: "Change Hue", sub: "Recolor grade", Icon: Palette, color: "#E8D3FF" },
  { k: "static_emoji", label: "Static Emoji", sub: "Pin a sticker", Icon: Smile, color: "#9EFFB8" },
  { k: "animate_emoji", label: "Animate Emoji", sub: "Motion sticker", Icon: Sparkles, color: "#FFD700" },
  { k: "fx", label: "FX Presets", sub: "More soon", Icon: Wand2, color: "#FF9E9E" },
];

function EffectsGrid() {
  const [preview, setPreview] = useState(null);
  return (
    <div className="relative h-full w-full flex flex-col" data-testid="stitcher-effects-grid">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4">
        <p className="font-mono text-[10px] text-white/60 leading-relaxed border-2 border-dashed border-[#2a2a2a]/30 rounded-xl p-2.5 mb-4">
          Position an animated overlay onto the video frame. Tap a module to open its config — wiring rolls out per tool.
        </p>
        <div className="grid grid-cols-3 gap-2.5">
          {EFFECTS.map((fx) => (
            <button key={fx.k} onClick={() => setPreview(fx)} data-testid={`stitcher-effect-${fx.k}`}
              className="aspect-square rounded-2xl border-2 border-black flex flex-col items-center justify-center gap-1.5 p-2 text-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform duration-150"
              style={{ backgroundColor: fx.color }}>
              <span className="w-9 h-9 rounded-xl border-2 border-black bg-[#141414]/60 grid place-items-center"><fx.Icon size={18} strokeWidth={2.2} style={{ color: fx.color }} /></span>
              <p className="font-serif text-sm leading-tight text-black">{fx.label}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Effect simulation preview modal (blank keys / uninitialised canvas tools) */}
      {preview && (
        <div className="absolute inset-0 z-40 bg-black/70 flex items-end" data-testid="effect-preview-modal" onClick={() => setPreview(null)}>
          <div className="w-full bg-black border-t-4 border-black rounded-t-3xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 pb-3 border-b-2 border-black/10 mb-3">
              <span className="w-9 h-9 rounded-xl border-2 border-black grid place-items-center" style={{ backgroundColor: preview.color }}><preview.Icon size={18} className="text-black" /></span>
              <h3 className="font-serif text-xl text-white flex-1">{preview.label}</h3>
              <button onClick={() => setPreview(null)} data-testid="effect-preview-close" className="w-8 h-8 grid place-items-center border-2 border-black bg-[#141414] shadow-[1.5px_1.5px_0px_#000]"><X size={15} /></button>
            </div>
            <div className="aspect-video w-full border-2 border-black bg-charcoal grid place-items-center mb-3">
              <preview.Icon size={40} strokeWidth={1.6} className="text-white/30" />
            </div>
            <div className="border-2 border-black bg-gold px-3 py-2.5 shadow-[3px_3px_0px_#000] text-center" data-testid="effect-sim-banner">
              <span className="font-mono text-[10px] font-black uppercase tracking-[0.12em] text-white">🧪 Effect Simulation Active - Preview Modal Only</span>
            </div>
            <p className="mt-2 font-mono text-[9px] text-white/50 leading-relaxed text-center">Advanced web-canvas editing for {preview.label} is being wired in — this is a safe preview.</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------------- SUITE --------------------------------- */
const MACROS = [
  { k: "stitcher", label: "Video Joiner", Icon: Film },
  { k: "effects", label: "Effects", Icon: Sparkles },
];

export default function MistStitcher() {
  const [macro, setMacro] = useStudioParam("view", "stitcher");
  return (
    <div className="h-full w-full flex flex-col" data-testid="mist-stitcher-suite">
      <div className="shrink-0 px-4 pt-3 pb-2">
        <div className="grid grid-cols-2 gap-1.5 bg-charcoal p-1 rounded-2xl border-2 border-[#2a2a2a]" data-testid="stitcher-macro-switch">
          {MACROS.map((mm) => (
            <button key={mm.k} onClick={() => setMacro(mm.k)} data-testid={`stitcher-macro-${mm.k}`}
              className={`flex items-center justify-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-[0.08em] rounded-xl py-2 transition-colors ${macro === mm.k ? "bg-gold text-black" : "text-white/60"}`}>
              <mm.Icon size={13} /> {mm.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {macro === "effects" ? <EffectsGrid /> : <FacebookComposer />}
      </div>
    </div>
  );
}
